/* global G */
/* eslint-disable no-unused-vars */
import { curry } from 'lib/util'
import { get, set } from 'lib/sequence/component/state/value'
import asObject from 'lib/sequence/component/children/asObject'
import sequenceComponentState from 'lib/sequence/component/state'
import { hidden } from 'lib/sequence/component/state/hidden'
import showToast from 'app/_shared/component/showToast'
import { settings } from 'app/_shared/session'

const {
  get: getSelection,
  set: setSelection,
} = sequenceComponentState('selection')

/**
 * Displays a toast informing the user that the previously set supportedBy organisation has been
 * automatically removed.
 *
 * @param {Gaia.AppModule.Spec} module  the current module composition object
 */
const showOrganisationClearedToast = async (module) => {
  await showToast(module, {
    message: {
      key: 'data.supportedByDeleted',
      ns: 'common',
      defaultValue: 'The previous service organisation has been cleared.',
    },
  })
}

/**
 * Displays a toast informing the user that the previously set supportedBy organisation has been
 * automatically changed.
 *
 * @param {Gaia.AppModule.Spec} module  the current module composition object
 */
const showOrganisationChangedToast = async (module) => {
  await showToast(module, {
    message: {
      key: 'data.supportedByChanged',
      ns: 'common',
      defaultValue: 'The previous service organisation has been changed.',
    },
  })
}

/**
 * Retrieves applicable supportedBy organisations based on country and organisation types, prefills
 * the supportedBy field, and updates the UI.
 *
 * @param {Gaia.AppModule.Spec} module  the current module composition object
 * @param {Gaia.Component.Spec} component  the current action's main component
 * @param {Event} event                 the event object that triggered this handler
 *
 * @returns {Promise<void>}
 */
const prefillSupportedBy = async (module, component, event) => {
  const context = module[G.ADAPTER][G.SESSION][G.STATE]?.[G.CONTEXT]
  const actionComponent = module[G.STATE][G.ACTION][G.COMPONENT]
  const { supportedBy: supportedByField } = asObject(actionComponent[G.CHILDREN])

  // Field may already have been hidden due to acl check, despite the setting
  if ((settings.suppressSupportedBy && context !== 'adm') || hidden(supportedByField)) {
    return
  }

  /**
   * Getting `type` and `country`.
   *
   * This handler may be called from various modules, therefore use
   * the action component instead of model for retrieving values.
   */
  const { address, type: typeField } = asObject(actionComponent[G.CHILDREN])
  const { country: countryField } = asObject(address[G.CHILDREN])
  const type = typeField[G.STATE].value || ''
  const [{ key: country = '' } = {}] = countryField[G.STATE].value || [{}]

  const { version } = module[G.MODEL][G.PROPS]
  const { api: { listing } } = supportedByField[G.PROPS]
  const params = { country, type }
  const url = `/api/v${version}/list/${listing}`
  const hasValue = !!get(supportedByField)?.[0]
  const selection = getSelection(supportedByField) || []

  try {
    const remoteAdapter = module[G.MODEL][G.ADAPTER][G.HTTP]
    const result = await remoteAdapter.post({ url, params })

    const selectionContainsValue = selection
      .find(option => result.value.find(value => option.key === value.key))

    /**
     * Depending on what we are getting back from the server,
     * we need to set `value` and `selection` in
     * `supportedByField`'s state accordingly.
     */
    // No items returned
    !result.value.length
      && setSelection(supportedByField, [])
      && set(supportedByField, null)
      && hasValue
      && await showOrganisationClearedToast(module)

    // One item returned
    result.value.length === 1
      && setSelection(supportedByField, result.value)
      && (!hasValue || !selectionContainsValue)
      && set(supportedByField, [result.value[0]])
      && await showOrganisationChangedToast(module)

    // Multiple items returned
    result.value.length > 1
      && setSelection(supportedByField, result.value)
      && !selectionContainsValue
      && set(supportedByField, null)
      && hasValue
      && await showOrganisationClearedToast(module)

    // Update UI
    module[G.ADAPTER][G.UI].update(module)
  } catch (e) {
    console.error('search:: tried and failed getting remote resources', url)
  }
}

export default curry(prefillSupportedBy)
